import React from "react";
import { Box, Link } from "@mui/material";
import { styled } from "@mui/material/styles";
import { PlaceRounded, PhoneRounded, OpenInNewRounded } from "@mui/icons-material";

interface LocationCardHeaderProps {
  name: string;
  address: string;
  phone?: string;
  /** When set, the name links here in a new tab (e.g. the public listing). */
  href?: string;
}

const HeaderRoot = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
});

const NameAndAddress = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 4,
  minWidth: 0,
});

const LocationName = styled(Box)({
  fontSize: "1.0714rem", // 15px at 14px root
  fontWeight: 700,
  color: "#1a2636",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  minWidth: 0,
});

const NameLink = styled(Link)({
  display: "flex",
  alignItems: "center",
  gap: 4,
  // Hug the text so the clickable area doesn't stretch across the column.
  alignSelf: "flex-start",
  maxWidth: "100%",
  minWidth: 0,
  color: "#1a2636",
});

const AddressRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "0 10px",
  fontSize: "0.8929rem", // 12.5px at 14px root
  color: "#5577aa",
  marginTop: 2,
});

const AddressItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 4,
});

/**
 * Location name + address + optional phone row at the top of `LocationCard.Content`.
 * Accepts individual string props — not the full `ILocationData` object.
 */
const LocationCardHeader = ({ name, address, phone, href }: LocationCardHeaderProps) => (
  <HeaderRoot>
    <NameAndAddress>
      {href ? (
        <NameLink
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          aria-label={`${name} (opens in a new tab)`}
        >
          <LocationName title={name}>{name}</LocationName>
          <OpenInNewRounded sx={{ fontSize: "0.8571rem", color: "#19C6F0", flexShrink: 0 }} aria-hidden="true" />
        </NameLink>
      ) : (
        <LocationName title={name}>{name}</LocationName>
      )}
      <AddressRow>
        <AddressItem>
          <PlaceRounded sx={{ fontSize: "0.8571rem", color: "#19C6F0" }} aria-hidden="true" />
          <span>{address}</span>
        </AddressItem>
        {phone && (
          <AddressItem>
            <PhoneRounded sx={{ fontSize: "0.8571rem", color: "#19C6F0" }} aria-hidden="true" />
            <span>{phone}</span>
          </AddressItem>
        )}
      </AddressRow>
    </NameAndAddress>
  </HeaderRoot>
);

export default LocationCardHeader;

